import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { searchCitiesAction } from "actions/regionsActions";
import { searchNeevayNetwork } from "actions/searchActions";
import { GET_VENDOR_TAXONOMY_URL } from "config/services";
import axios from "utils/axios";
import { buildVendorCategoryFilter, findTaxonomyMatch, findTaxonomySuggestions, getVendorCategoryType } from "../utils/vendorCategorySearch";

const MOST_POPULAR_SEARCHES_DATA = {
  Contractors: ["Civil", "Plumbing", "Waterproofing", "EPC", "Labour"],
  Suppliers: [
    "Cement",
    "TMT Bar",
    "Conveyor",
    "Electrical",
    "Building Materials",
  ],
  Consultants: [
    "RCC",
    "Electrical",
    "Project Management",
    "MK Design",
    "Civil",
  ],
};

const MOST_POPULAR_SEARCH_CITIES = ["Pune", "Mumbai", "Delhi", "Bengaluru"];

const SearchNew = ({
  compact = false,
  externalVendorType = "",
  externalFilterStr = "",
  externalFacetValue = "",
  externalFacetValues = [],
}) => {
  const cities = useSelector((state) => state.regions.cities);
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  // const [regions, setRegions] = useState(cities);
  const [vendorClicked, setVendorClicked] = useState(false);
  const [tradeClicked, setTradeClicked] = useState(false);
  const [regionClicked, setRegionClicked] = useState(false);
  const [selectedVendorType, setSelectedVendorType] = useState(""); //value selected in vendor type
  const [selectedTradeOrBusiness, setSelectedTradeOrBusiness] = useState(""); //value selected in trade or business
  const [selectedRegion, setSelectedRegion] = useState(""); //value selected in region
  const [showLeftDivider, setShowLeftDivider] = useState(true);
  const [showRightDivider, setShowRightDivider] = useState(true);
  const [selectedSection, setSelectedSection] = useState(""); //color of selected div
  const [vendorTaxonomyData, setVendorTaxonomyData] = useState({});
  const [selectedTaxonomySuggestion, setSelectedTaxonomySuggestion] = useState(null);

  const dropdownRef = useRef(null);
  const [searchInputPlaceHolder, setSearchInputPlaceHolder] = useState("Search By Material Or Service");
  const initialRegions = [...MOST_POPULAR_SEARCH_CITIES, ...cities];

  const [regions, setRegions] = useState(initialRegions);
  const activeVendorType = externalVendorType || selectedVendorType;
  const activeVendorCategoryType = getVendorCategoryType(activeVendorType);
  const taxonomySuggestions = useMemo(
    () => findTaxonomySuggestions(
      vendorTaxonomyData,
      activeVendorCategoryType,
      selectedTradeOrBusiness,
    ),
    [activeVendorCategoryType, selectedTradeOrBusiness, vendorTaxonomyData],
  );
  const showTradeSuggestions = tradeClicked && selectedTradeOrBusiness.trim();

  const getSearchPlaceholder = (vendorType) => {
    switch (vendorType) {
      case "Material Supplier":
        return "Search by material";
      case "Labour Contractor":
        return "What type of Skill?";
      case "Contractor (Providing and Fixing)":
        return "Search by Service";
      case "Consultant":
        return "What type of Work?";
      default:
        return "Search By Material Or Service";
    }
  };

  useEffect(() => {
    const initialRegions = [...new Set([...MOST_POPULAR_SEARCH_CITIES, ...cities])];
    setRegions(initialRegions);
  }, [cities]);

  useEffect(() => {
    let isMounted = true;

    axios
      .get(GET_VENDOR_TAXONOMY_URL)
      .then((response) => {
        if (isMounted) {
          setVendorTaxonomyData(response?.data?.data || {});
        }
      })
      .catch(() => {
        if (isMounted) {
          setVendorTaxonomyData({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);
  
  const handleChangeRegionValue = (e) => {
    const value = e.target.value;
    const searchTerm = value
      ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
      : "";
    setSelectedRegion(searchTerm);
  
    // Filter the initialRegions array directly
    const filterCities = initialRegions.filter((city) =>
      city.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setRegions([...new Set(filterCities)]);
  };

  const getSuggestionPath = (suggestion = {}) => {
    if (suggestion.level === "discipline") return suggestion.type;
    if (suggestion.level === "category") {
      return [suggestion.type, suggestion.disciplineName].filter(Boolean).join(" / ");
    }
    return [suggestion.type, suggestion.disciplineName, suggestion.categoryName]
      .filter(Boolean)
      .join(" / ");
  };

  const getSuggestionLabel = (level = "") => {
    if (level === "subCategory") return "Sub-category";
    if (level === "category") return "Category";
    return "Discipline";
  };

  const handleSelectedTaxonomySuggestion = (suggestion) => {
    setSelectedTaxonomySuggestion(suggestion || null);
    setSelectedTradeOrBusiness(suggestion?.name || "");
    setTradeClicked(false);
    setShowLeftDivider(true);
    setShowRightDivider(false);
  };
  const handleSearchClick = (e) => {
    if (
      (!activeVendorType && !selectedTaxonomySuggestion?.type) ||
      selectedTradeOrBusiness === "" ||
      selectedRegion === ""
    ) {
      setError(true);
      setShowLeftDivider(true);
      setShowRightDivider(true);
      setSelectedSection("");
    } else {
      setError(false);
      const vendorCategoryType = selectedTaxonomySuggestion?.type || activeVendorCategoryType || "";
      const taxonomyMatch = selectedTaxonomySuggestion || findTaxonomyMatch(
        vendorTaxonomyData,
        vendorCategoryType,
        selectedTradeOrBusiness,
      );
      const taxonomyFilter = taxonomyMatch
        ? buildVendorCategoryFilter({ type: vendorCategoryType, ...taxonomyMatch })
        : "";
      const vendorTypeFilterStr = selectedVendorType
        ? selectedVendorType === "Suppliers"
          ? `businessProfile.businessCategory_str:("Supplier")`
          : `businessProfile.businessCategory_str:("${selectedVendorType}")`
        : "";
      const fallbackFilterStr = externalFilterStr || vendorTypeFilterStr;
      const filterStr = taxonomyFilter || fallbackFilterStr;
      const categoryFacetValue =
        selectedVendorType === "Suppliers" ? "Supplier" : selectedVendorType;
      const derivedFacetValue = selectedTaxonomySuggestion?.type || categoryFacetValue;
      const categoryFacetValues =
        externalFacetValues.length > 0
          ? externalFacetValues
          : [externalFacetValue || derivedFacetValue].filter(Boolean);

      dispatch(
        searchNeevayNetwork({
          searchKeyWord: taxonomyFilter ? "" : selectedTradeOrBusiness,
          solrSearchKeyWord: taxonomyFilter ? "" : undefined,
          regionString: selectedRegion,
          filterStr,
          facetTitle: "Category",
          facetValue: categoryFacetValues[0],
          facetValues: categoryFacetValues,
          isSelected: true,
          subCategory: taxonomyMatch?.name || selectedTradeOrBusiness,
        })
      );

      if (window.location.pathname === "/neevay-network") {
        const params = new URLSearchParams({
          searchKey: taxonomyMatch?.name || selectedTradeOrBusiness,
          city: selectedRegion,
          category: vendorCategoryType || derivedFacetValue || "",
          type: vendorCategoryType === "Material" ? "material" : "contractor",
        });

        if (taxonomyMatch?.disciplineId) params.set("disciplineId", taxonomyMatch.disciplineId);
        if (taxonomyMatch?.categoryId) params.set("categoryId", taxonomyMatch.categoryId);
        if (taxonomyMatch?.subCategoryId) params.set("subCategoryId", taxonomyMatch.subCategoryId);

        window.history.pushState({}, "", `/neevay-network?${params.toString()}`);
      }
      setSelectedSection("");
    }
  };
  const handleVendorDropdown = () => {
    setVendorClicked(!vendorClicked); // Open vendor dropdown
    setTradeClicked(false); // Close trade dropdown
    setRegionClicked(false); // Close region dropdown
    setSelectedSection("vendor");
  };

  const handleVendorHover = () => {
    setShowLeftDivider(false);
  };
  const handleVendorNonHover = () => {
    setShowLeftDivider(true);
  };

  const handleTradeDropdown = () => {
    setTradeClicked(true);
    setVendorClicked(false);
    setRegionClicked(false);
    setShowLeftDivider(false);
    setShowRightDivider(false);
    setSelectedSection("trade");
  };

  const handleTradeOrBusinessHover = () => {
    setShowLeftDivider(false);
    setShowRightDivider(false);
  };
  const handleTradeOrBusinessNonHover = () => {
    setShowLeftDivider(true);
    setShowRightDivider(true);
  };

  const handleRegionDropdown = () => {
    setRegionClicked(!regionClicked);
    setVendorClicked(false);
    setTradeClicked(false);
    setSelectedSection("region");
    setShowLeftDivider(true);
  };

  const handleRegionHover = () => {
    setShowRightDivider(false);
  };
  const handleRegionNonHover = () => {
    setShowRightDivider(true);
  };

  const handleSelectedVendorType = (value) => {
    setSelectedTaxonomySuggestion(null);
    setSelectedTradeOrBusiness("");
    setSelectedVendorType(value);
    setVendorClicked(!vendorClicked);
    setShowLeftDivider(true);
    setShowRightDivider(true);

    setSearchInputPlaceHolder(getSearchPlaceholder(value));
  };

  const handleSelectedTradeOrBusiness = (value) => {
    setSelectedTradeOrBusiness(value);
    setTradeClicked(false);
    setShowLeftDivider(true);
    setShowRightDivider(false);
  };

  const handleSelectedRegion = (value) => {
    setSelectedRegion(value);
    setRegionClicked(!regionClicked);
    setShowLeftDivider(true);
    setShowRightDivider(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchClick();
      e.preventDefault();
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setVendorClicked(false);
        setRegionClicked(false);
        setTradeClicked(false);
        setShowLeftDivider(true);
        setShowRightDivider(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    if (
      (activeVendorType !== "" || selectedTaxonomySuggestion?.type) &&
      selectedTradeOrBusiness !== "" &&
      selectedRegion !== ""
    ) {
      setError(false);
    }
  }, [activeVendorType, selectedTaxonomySuggestion, selectedTradeOrBusiness, selectedRegion]);

  useEffect(() => {
    dispatch(searchCitiesAction());
  }, [dispatch]);

  useEffect(() => {
    if (externalVendorType) {
      setSearchInputPlaceHolder(getSearchPlaceholder(externalVendorType));
      setSelectedVendorType("");
      setVendorClicked(false);
    }
  }, [externalVendorType]);

  return (
    <div
      className={`flex ${compact ? "min-h-[42px]" : "min-h-[46px]"} w-full items-center justify-start gap-0.5 border bg-white/95 ${compact ? "max-w-none rounded-md px-0.5 shadow-sm" : "max-w-[740px] rounded-md px-1 shadow-sm"} ${selectedSection === "" ? "border-[#F0D8CA]" : "border-[#E8D8CF] bg-[#FFF8F3]"
        } ${error
          ? "border-[1px] border-[#EB5A4F] ring ring-red-200"
          : ""
        }`}
    >
      {/* vendor type */}
      {!compact && <div
        onMouseEnter={handleVendorHover}
        onMouseLeave={handleVendorNonHover}
        onClick={handleVendorDropdown}
        className="relative shrink-0"
        // style={{ backgroundColor: vendorBgColor, borderTopLeftRadius: "50px", borderBottomLeftRadius: "50px" }}
        style={{ borderTopLeftRadius: "50px", borderBottomLeftRadius: "50px" }}
      >
        <div
            className={`relative flex h-[36px] w-[165px] items-center rounded-md px-2 hover:rounded-md ${selectedSection === "vendor" ? "bg-white" : "hover:bg-[#FFF4EE]"
            }`}
        >
          <img
            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/user_icon_searchbar.svg"
            alt="Vendor Type"
            className="h-4 w-4 bg-transparent opacity-80"
            style={{ filter: "grayscale(1) brightness(0.58) contrast(0.92)" }}
          />
          <input
            type="text"
            className="mr-1 w-full overflow-x-auto border-none bg-transparent px-[8px] text-xs font-medium text-[#5f5f5f] placeholder:text-xs placeholder:font-medium placeholder-[#5F5F5F] focus:ring-transparent"
            value={selectedVendorType}
            onKeyDown={(e) => e.preventDefault()}  // Disables typing
            placeholder="Vendor Type"
            readOnly  // Prevents typing
          />
          <img
            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/searchbar_arrow.svg"
            alt="Dropdown Arrow"
            className={`w-3 h-3  transform ${vendorClicked ? "rotate-180" : ""
              }`}
          />
          {!selectedVendorType.trim() && error && (
            <Tooltip
              title="This field is required"
              arrow
              placement="top-end"
              open
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 500 }}
            >
              <img
                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/circle_exclamation_solid_org.svg"
                alt="Error"
                className="absolute right-3 top-[13px] h-4 w-4"
              />
            </Tooltip>
          )}
          {vendorClicked && (
            <ul
              ref={dropdownRef}
              className="absolute top-[42px] z-50 min-w-[215px] cursor-default rounded-md border border-[#E8E8E0] bg-[#fff] py-2 text-xs shadow-lg"
            >
              {[
                "Contractor (Providing and Fixing)",
                "Labour Contractor",
                "Material Supplier",
                "Consultant",
              ].map((name) => (
                <li
                  key={name}
                  onClick={() => handleSelectedVendorType(name)}
                  className="mx-2 truncate rounded-md px-2 py-1 hover:bg-[#FFF4EE] hover:text-[#B43A12]"
                >
                  {name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>}
      {/* what type of work */}
      <div
        onMouseEnter={handleTradeOrBusinessHover}
        onMouseLeave={handleTradeOrBusinessNonHover}
        onClick={handleTradeDropdown}
        className="relative flex min-w-0 flex-1 ml-1 items-center"
      // style={{ backgroundColor: tradeBgColor }}
      >
        {showLeftDivider &&
          !compact &&
          selectedSection !== "trade" &&
          selectedSection !== "vendor" && (
            <p className="pb-1 text-black absolute top-1 left-0">|</p>
          )}
        <div
          className={`flex ${compact ? "h-[32px]" : "h-[36px]"} min-w-[200px] flex-1 items-center hover:rounded-md ${selectedSection === "trade"
            ? "rounded-md bg-white"
            : "hover:bg-[#FFF4EE]"
            }`}
        >
          <img
            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/N2_searchbar_magnifying_glass.svg"
            alt="Search"
            className={`${compact ? "ml-2.5 h-4 w-4" : "ml-3 h-4 w-4"} opacity-90`}
          />
          <input
            type="text"
            className={`${compact ? "h-7 text-xs placeholder:text-xs" : "h-6 text-xs placeholder:text-xs"} w-full border-none bg-transparent px-[8px] font-medium text-[#5f5f5f] placeholder:font-medium placeholder-[#5F5F5F] focus:ring-transparent`}
            value={selectedTradeOrBusiness}
            onKeyDown={handleKeyDown}
            onFocus={() => setTradeClicked(true)}
            onChange={(e) => {
              setSelectedTaxonomySuggestion(null);
              setSelectedTradeOrBusiness(e.target.value);
              setTradeClicked(true);
            }}
            placeholder={searchInputPlaceHolder}
          />
          {!selectedTradeOrBusiness.trim() && error && (
            <Tooltip
              title="This field is required"
              arrow
              placement="top-end"
              open
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 500 }}
            >
              <img
                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/circle_exclamation_solid_org.svg"
                alt="Error"
                className="absolute right-3 top-[13px] h-4 w-4"
              />
            </Tooltip>
          )}
          {false && tradeClicked && selectedVendorType !== "" && (
            <ul
              ref={dropdownRef}
              className="absolute top-[42px] z-50 w-[270px] cursor-default rounded-3xl border border-[#E8E8E0] bg-[#fff] py-4 text-xs font-medium shadow-lg"
            >
              <li className="pl-6 pr-14 ml-2 mr-2 mb-2 text-xs font-semibold">
                You can search for
              </li>
              {MOST_POPULAR_SEARCHES_DATA[selectedVendorType]?.map((name) => (
                <li
                  key={name}
                  onClick={() => handleSelectedTradeOrBusiness(name)}
                  className="ml-2 mr-2 rounded-md py-1 pl-6 pr-14 hover:bg-[#FFF4EE] hover:text-[#B43A12]"
                >
                  {name}
                </li>
              ))}
            </ul>
          )}
        </div>
          {showTradeSuggestions && (
            <div
              ref={dropdownRef}
              className="absolute left-0 top-[42px] z-50 max-h-[280px] w-[420px] overflow-auto rounded-xl border border-[#E8D8CF] bg-white py-2 text-xs shadow-[0_18px_42px_rgba(17,24,39,0.16)]"
            >
              {taxonomySuggestions.length > 0 ? (
                taxonomySuggestions.map((suggestion) => (
                  <button
                    key={`${suggestion.level}-${suggestion.disciplineId || ""}-${suggestion.categoryId || ""}-${suggestion.subCategoryId || ""}`}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelectedTaxonomySuggestion(suggestion);
                    }}
                    className="flex w-full flex-col items-start gap-1 px-3 py-2 text-left transition hover:bg-[#FFF4EE]"
                  >
                    <div className="flex w-full min-w-0 items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-[#303026]">
                        {suggestion.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-[#FFF1E8] px-2 py-0.5 text-[10px] font-semibold text-[#B43A12]">
                        {getSuggestionLabel(suggestion.level)}
                      </span>
                    </div>
                    <span className="max-w-full truncate text-[11px] font-medium text-[#7A8597]">
                      {getSuggestionPath(suggestion)}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-xs font-medium text-[#7A8597]">
                  No matching category found
                </div>
              )}
            </div>
          )}
      </div>

      {/* Region */}
      <div
        onMouseEnter={handleRegionHover}
        onMouseLeave={handleRegionNonHover}
        onClick={handleRegionDropdown}
        className="relative shrink-0"
        // style={{ backgroundColor: regionBgColor, borderTopRightRadius: "50px", borderBottomRightRadius: "50px" }}
        style={{
          borderTopRightRadius: "50px",
          borderBottomRightRadius: "50px",
        }}
      >
        {showRightDivider &&
          !compact &&
          selectedSection !== "trade" &&
          selectedSection !== "region" && (
            <p className="pb-1 absolute top-1 text-black">|</p>
          )}
        <div
          className="flex items-center gap-2 pr-1"
        >
          <div className={`flex ${compact ? "h-[32px]" : "h-[36px]"} w-[135px] items-center rounded-md hover:rounded-md ${selectedSection === "region" ? "bg-white" : "hover:bg-[#FFF4EE]"
            }`}>
          <img
            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/searchbar_location_nn.svg"
            alt="Location"
            className={`${compact ? "ml-2 h-4 w-4" : "ml-2.5 h-4 w-4"} opacity-90`}
          />
          <input
            type="text"
            className={`${compact ? "mr-0.5 h-7" : "mr-1 h-6"} w-[100px] overflow-x-auto border-none bg-transparent px-[8px] text-xs font-medium text-[#5f5f5f] placeholder:text-xs placeholder:font-medium placeholder-[#5F5F5F] focus:ring-transparent`}
            value={selectedRegion}
            onKeyDown={handleKeyDown}
            onChange={(e) => handleChangeRegionValue(e)}
            placeholder="Region"
          />
          {!selectedRegion.trim() && error && (
            <Tooltip
              title="This field is required"
              arrow
              open
              placement="top-end"
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 500 }}
            >
              <img
                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/circle_exclamation_solid_org.svg"
                alt="Error"
                className="absolute right-[132px] top-[13px] h-4 w-4"
              />
            </Tooltip>
          )}
          </div>
          {regionClicked && (
            <ul
              ref={dropdownRef}
              className="absolute top-[42px] z-50 max-h-[158px] w-[190px] cursor-default overflow-auto rounded-2xl border border-[#E8E8E0] bg-[#fff] py-2 text-xs shadow-lg"
            >
              {regions?.map((city) => (
                <li
                  key={city}
                  onClick={() => handleSelectedRegion(city)}
                  className="mx-2 rounded-md px-2 py-1 hover:bg-[#FFF4EE] hover:text-[#B43A12]"
                >
                  {city}
                </li>
              ))}
              {regions?.length === 0 && (
                <li
                  // onClick={() => handleSelectedRegion(city)}
                  className="py-1 px-2  ml-2 mr-2 text-gray-600"
                >
                  No city found
                </li>
              )}
            </ul>
          )}
          {/* Search button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Stop the event from bubbling up
              handleSearchClick();
            }}
            className={`flex ${compact ? "h-[32px] min-w-[92px]" : "h-[36px] min-w-[100px]"} items-center justify-center rounded-md bg-[linear-gradient(135deg,#FD6600_0%,#E95420_100%)] px-3 text-white shadow-[0_8px_20px_rgba(233,84,32,0.28)] transition duration-300 hover:brightness-95`}
          >
            <img
              src="https://storagereponeevaydevcdn.blob.core.windows.net/business/searchbar_white.svg"
              alt="Search"
              className={`${compact ? "h-3.5 w-3.5" : "w-[14px] h-[14px]"}`}
            />
            <span className={`${compact ? "pl-1 text-[11px]" : "pl-1 text-xs"} font-medium`}>Search</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchNew;




